import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {API_BASE_URL} from "../../../config/api";

const Category = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/categories/allcategory`);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        // Filter to only show categories where categoryFallsOn is "Main"
        const mainCategories = data.filter(category => category.categoryFallsOn === "Main");
        setCategories(mainCategories);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleCategoryClick = (category) => {
    setActiveCategory(category._id);
    
    // Add a small timeout to show the click animation before navigating
    setTimeout(() => {
      navigate(`/${category.categoryType}/${category.categoryName}`);
    }, 300);
  };

  if (loading) {
    return <div className="w-full py-8 text-center">Loading categories...</div>;
  }

  if (error) {
    return <div className="w-full py-8 text-center text-red-500">Error: {error}</div>;
  }

  // Function to create layout groups
  const createLayoutGroups = () => {
    if (!categories || categories.length === 0) return [];
    
    // Create different layout sections based on the image template
    const topRow = categories.slice(0, 3);
    const middleRow = categories.slice(3, 5);
    const bottomRow = categories.slice(5, 9);
    
    return { topRow, middleRow, bottomRow };
  };

  const { topRow, middleRow, bottomRow } = createLayoutGroups();

  return (
    <div className="w-full py-8 px-4">
      {/* Top row - 3 equal columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {topRow.map((category, index) => (
          <div
            key={category._id}
            className={`relative h-64 cursor-pointer overflow-hidden rounded-lg shadow-md transition-all duration-300 transform hover:shadow-xl ${activeCategory === `top-${index}` ? 'scale-95' : 'hover:scale-105'}`}
            onClick={() => handleCategoryClick(category)}
          >
            <img
              src={category.categoryImage}
              alt={category.categoryName}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
              onError={(e) => {
                e.target.onerror = null;
               
              }}
            />
            <div className="absolute inset-0 flex flex-col justify-end items-center p-4 bg-gradient-to-t from-black to-transparent opacity-80 hover:opacity-90 transition-opacity duration-300">
              <h3 className="text-white font-bold text-lg transform transition-transform duration-300 hover:translate-y-1">{category.categoryName}</h3>
              <p className="text-white text-sm">
                {index === 0 ? "Suit for every occasion" : 
                 index === 1 ? "Show your personality" : 
                 "Comfortable than you think"}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Middle row - 2 equal columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {middleRow.map((category, index) => (
          <div
            key={category._id}
            className={`relative h-56 cursor-pointer overflow-hidden rounded-lg shadow-md transition-all duration-300 transform ${activeCategory === `mid-${index}` ? 'scale-95' : 'hover:scale-102'}`}
            onClick={() => handleCategoryClick(category)}
          >
            <img
              src={category.categoryImage }
              alt={category.categoryName}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
              onError={(e) => {
                e.target.onerror = null;
              
              }}
            />
            <div className="absolute inset-0 flex flex-col justify-center items-center p-4 bg-gradient-to-b from-transparent via-black/30 to-transparent hover:bg-black/40 transition-all duration-300">
              <h3 className="text-white font-bold text-xl transform transition-transform duration-300 hover:scale-110">
                {index === 0 ? "Feeling is Believing" : "You got loved"}
              </h3>
              <p className="text-white text-sm text-center">
                {index === 0 ? "Fabric changes how you feel" : "Something for them too(?)"}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom row - 4 equal columns */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {bottomRow.map((category, index) => (
          <div
            key={category._id}
            className={`relative h-48 cursor-pointer overflow-hidden rounded-lg shadow-md transition-all duration-300 transform ${activeCategory === `bottom-${index}` ? 'scale-95' : 'hover:scale-105'}`}
            onClick={() => handleCategoryClick(category)}
          >
            <img
              src={category.categoryImage }
              alt={category.categoryName}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
              onError={(e) => {
                e.target.onerror = null;
         
              }}
            />
            <div className="absolute inset-0 flex flex-col justify-end items-center p-2 bg-gradient-to-t from-black to-transparent opacity-80 hover:opacity-90 transition-opacity duration-300">
              <h3 className="text-white font-bold transform transition-transform duration-300 hover:translate-y-1">{getCategoryLabel(index)}</h3>
              <p className="text-white text-xs">
                {index === 0 ? "Look at our newest styles" : 
                 index === 1 ? "Find the perfect pair" : 
                 index === 2 ? "Tops for every occasion" : 
                 "Party time!"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to get category labels for the bottom row
function getCategoryLabel(index) {
  switch (index) {
    case 0: return "Dresses";
    case 1: return "Tops";
    case 2: return "Tees";
    case 3: return "Skirts";
    default: return "Category";
  }
}

export default Category;